import React from 'react'
import { Helmet } from 'react-helmet'
import utils from '../utils'
import { FaCookie, FaDatabase, FaEnvelope, FaEye, FaLock, FaPhone, FaUserShield } from 'react-icons/fa6'
import { FaMapMarkerAlt, FaShieldAlt } from 'react-icons/fa'

function PrivacyPage(props) {
    const url = window.location.href
    return (
        <div>
            <Helmet>
                <title>Privacy Policy - {utils.siteName}</title>
                <link rel="canonical" href={url} />
                <meta
                    name="description"
                    content="HealthConnect Privacy Policy. Learn how we collect, use, and protect your personal and health information. Your privacy and security are our top priorities."
                />
                <meta
                    property="og:title"
                    content="Privacy Policy - HealthConnect"
                />
                <meta
                    property="og:description"
                    content="Your privacy matters. Read our comprehensive policy on how we handle your personal and health data securely."
                />
                <meta
                    property="og:type"
                    content="website"
                />
                <meta
                    property="og:url"
                    content={url}
                />
                <meta
                    name="keywords"
                    content="privacy policy, data protection, health data, patient privacy, Kenya healthcare, HealthConnect"
                />
            </Helmet>
            <section className="px-6 md:px-12 py-5 bg-base-100 relative overflow-hidden -mt-4">
                <div className="absolute -top-24 -right-24 size-96 bg-primary/10 rounded-full blur-3xl"></div>
                <div className="absolute -bottom-24 -left-24 size-96 bg-blue-400/10 rounded-full blur-3xl"></div>

                <div className="max-w-5xl mx-auto relative">
                    <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-bold uppercase tracking-wider mb-6">
                        <span className="size-2 rounded-full bg-primary mr-2 animate-pulse"></span>
                        Privacy Policy
                    </span>

                    <h2 className="text-3xl md:text-5xl font-black text-text-main dark:text-white leading-[1.1] tracking-tight mb-8">
                        Your Privacy <span className="text-primary">Matters to Us</span>
                    </h2>

                    <p className="text-lg text-text-secondary dark:text-slate-400 font-light max-w-2xl leading-relaxed">
                        We are committed to protecting your personal and health information. This policy explains how we collect, use, and safeguard your data when you use HealthConnect services.
                    </p>
                </div>
            </section>
            <section className="py-16 px-6">
                <div className="">
                    <div className="text-center mb-12">
                        <h2 className="text-2xl md:text-3xl font-black text-text-main dark:text-white mb-4">
                            Privacy <span className="text-primary">Principles</span>
                        </h2>
                    </div>

                    <div className="grid md:grid-cols-3 gap-6">
                        <div className="group bg-white dark:bg-slate-900 p-6 rounded-2xl border border-border-color dark:border-slate-800 shadow-sm hover:shadow-card-hover hover:border-primary/30 transition-all duration-300">
                            <div className="size-14 rounded-2xl bg-primary/20 text-primary flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                                <FaShieldAlt className="text-3xl" />
                            </div>
                            <h3 className="text-xl font-bold text-text-main dark:text-white mb-3">Data Protection</h3>
                            <p className="text-secondary dark:text-slate-400 text-sm leading-relaxed">
                                We use industry-standard encryption and security measures to protect your personal and health information from unauthorized access.
                            </p>
                        </div>

                        <div className="group bg-white dark:bg-slate-900 p-6 rounded-2xl border border-border-color dark:border-slate-800 shadow-sm hover:shadow-card-hover hover:border-primary/30 transition-all duration-300">
                            <div className="size-14 rounded-2xl bg-primary/20 text-primary flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                                <FaLock className="text-3xl" />
                            </div>
                            <h3 className="text-xl font-bold text-text-main dark:text-white mb-3">Confidentiality</h3>
                            <p className="text-secondary dark:text-slate-400 text-sm leading-relaxed">
                                Your health information is confidential. We only share data with your consent or as required by law.
                            </p>
                        </div>

                        <div className="group bg-white dark:bg-slate-900 p-6 rounded-2xl border border-border-color dark:border-slate-800 shadow-sm hover:shadow-card-hover hover:border-primary/30 transition-all duration-300">
                            <div className="size-14 rounded-2xl bg-primary/20 text-primary flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                                <FaUserShield className="text-3xl" />
                            </div>
                            <h3 className="text-xl font-bold text-text-main dark:text-white mb-3">Your Control</h3>
                            <p className="text-secondary dark:text-slate-400 text-sm leading-relaxed">
                                You have the right to access, correct, or delete your personal data. We give you full control over your information.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            <section className="py-20 px-6 bg-base-100">
                <div className="max-w-4xl mx-auto space-y-12">

                    <div className="group bg-white dark:bg-slate-900 p-8 rounded-2xl border border-border-color dark:border-slate-800 shadow-sm">
                        <div className="flex items-center gap-4 mb-6">
                            <div className="size-12 rounded-xl bg-primary/20 text-primary flex items-center justify-center">
                                <FaDatabase className="text-2xl" />
                            </div>
                            <h3 className="text-2xl font-black text-text-main dark:text-white">1. Information We Collect</h3>
                        </div>
                        <div className="space-y-4 text-secondary dark:text-slate-400">
                            <p>We collect information to provide you with the best healthcare services:</p>
                            <ul className="space-y-2 ml-4">
                                <li className="flex items-start gap-2">
                                    <span className="text-primary font-bold">•</span>
                                    <span><strong>Personal Information:</strong> Name, phone number, email address, physical address, date of birth</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-primary font-bold">•</span>
                                    <span><strong>Health Information:</strong> Medical conditions, symptoms, treatment history, medications, allergies</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-primary font-bold">•</span>
                                    <span><strong>Payment Information:</strong> Billing details, transaction history (processed securely through third-party providers)</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-primary font-bold">•</span>
                                    <span><strong>Usage Data:</strong> App usage patterns, device information, IP address, cookies</span>
                                </li>
                            </ul>
                        </div>
                    </div>

                    <div className="group bg-white dark:bg-slate-900 p-8 rounded-2xl border border-border-color dark:border-slate-800 shadow-sm">
                        <div className="flex items-center gap-4 mb-6">
                            <div className="size-12 rounded-xl bg-primary/20 text-primary flex items-center justify-center">
                                <FaEye className="text-2xl" />
                            </div>
                            <h3 className="text-2xl font-black text-text-main dark:text-white">2. How We Use Your Information</h3>
                        </div>
                        <div className="space-y-4 text-secondary dark:text-slate-400">
                            <p>Your information is used solely to improve your healthcare experience:</p>
                            <ul className="space-y-2 ml-4">
                                <li className="flex items-start gap-2">
                                    <span className="text-primary font-bold">•</span>
                                    <span>Match you with qualified nurses based on your health conditions and location</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-primary font-bold">•</span>
                                    <span>Facilitate communication between you and your healthcare provider</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-primary font-bold">•</span>
                                    <span>Process payments and send service-related notifications</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-primary font-bold">•</span>
                                    <span>Improve our services and develop new features</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-primary font-bold">•</span>
                                    <span>Comply with legal obligations and healthcare regulations</span>
                                </li>
                            </ul>
                        </div>
                    </div>

                    <div className="group bg-white dark:bg-slate-900 p-8 rounded-2xl border border-border-color dark:border-slate-800 shadow-sm">
                        <div className="flex items-center gap-4 mb-6">
                            <div className="size-12 rounded-xl bg-accent/20 text-accent flex items-center justify-center">
                                <FaUserShield className="text-2xl" />
                            </div>
                            <h3 className="text-2xl font-black text-text-main dark:text-white">3. Data Sharing & Disclosure</h3>
                        </div>
                        <div className="space-y-4 text-secondary dark:text-slate-400">
                            <p>We do not sell your personal information. We only share data in these situations:</p>
                            <ul className="space-y-2 ml-4">
                                <li className="flex items-start gap-2">
                                    <span className="text-accent font-bold">•</span>
                                    <span><strong>With Your Nurse:</strong> Relevant health information to provide appropriate care</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-accent font-bold">•</span>
                                    <span><strong>Service Providers:</strong> Payment processors, cloud storage, communication tools (bound by confidentiality)</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-accent font-bold">•</span>
                                    <span><strong>Legal Requirements:</strong> When required by law, court order, or government authority</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-accent font-bold">•</span>
                                    <span><strong>Emergency Situations:</strong> To protect your safety or the safety of others</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-accent font-bold">•</span>
                                    <span><strong>With Your Consent:</strong> Any other sharing requires your explicit permission</span>
                                </li>
                            </ul>
                        </div>
                    </div>

                    <div className="group bg-white dark:bg-slate-900 p-8 rounded-2xl border border-border-color dark:border-slate-800 shadow-sm">
                        <div className="flex items-center gap-4 mb-6">
                            <div className="size-12 rounded-xl bg-success/20 text-success flex items-center justify-center">
                                <FaLock className="text-2xl" />
                            </div>
                            <h3 className="text-2xl font-black text-text-main dark:text-white">4. Data Security</h3>
                        </div>
                        <div className="space-y-4 text-secondary dark:text-slate-400">
                            <p>We implement robust security measures to protect your data:</p>
                            <ul className="space-y-2 ml-4">
                                <li className="flex items-start gap-2">
                                    <span className="text-success font-bold">•</span>
                                    <span>End-to-end encryption for all data transmission</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-success font-bold">•</span>
                                    <span>Secure servers with regular security audits</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-success font-bold">•</span>
                                    <span>Access controls and authentication for staff</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-success font-bold">•</span>
                                    <span>Regular backups and disaster recovery plans</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-success font-bold">•</span>
                                    <span>Compliance with Kenya Data Protection Act, 2019</span>
                                </li>
                            </ul>
                        </div>
                    </div>

                    <div className="group bg-white dark:bg-slate-900 p-8 rounded-2xl border border-border-color dark:border-slate-800 shadow-sm">
                        <div className="flex items-center gap-4 mb-6">
                            <div className="size-12 rounded-xl bg-primary/20 text-primary flex items-center justify-center">
                                <FaCookie className="text-2xl" />
                            </div>
                            <h3 className="text-2xl font-black text-text-main dark:text-white">5. Cookies & Tracking</h3>
                        </div>
                        <div className="space-y-4 text-secondary dark:text-slate-400">
                            <p>We use cookies to enhance your experience:</p>
                            <ul className="space-y-2 ml-4">
                                <li className="flex items-start gap-2">
                                    <span className="text-primary font-bold">•</span>
                                    <span><strong>Essential Cookies:</strong> Required for app functionality and security</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-primary font-bold">•</span>
                                    <span><strong>Analytics Cookies:</strong> Help us understand how you use our services</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-primary font-bold">•</span>
                                    <span><strong>Preference Cookies:</strong> Remember your settings and preferences</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-primary font-bold">•</span>
                                    <span>You can control cookie settings through your browser or app settings</span>
                                </li>
                            </ul>
                        </div>
                    </div>

                    <div className="group bg-white dark:bg-slate-900 p-8 rounded-2xl border border-border-color dark:border-slate-800 shadow-sm">
                        <div className="flex items-center gap-4 mb-6">
                            <div className="size-12 rounded-xl bg-primary/20 text-primary flex items-center justify-center">
                                <FaUserShield className="text-2xl" />
                            </div>
                            <h3 className="text-2xl font-black text-text-main dark:text-white">6. Your Rights</h3>
                        </div>
                        <div className="space-y-4 text-secondary dark:text-slate-400">
                            <p>Under the Kenya Data Protection Act, you have the right to:</p>
                            <ul className="space-y-2 ml-4">
                                <li className="flex items-start gap-2">
                                    <span className="text-primary font-bold">•</span>
                                    <span>Access your personal data held by us</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-primary font-bold">•</span>
                                    <span>Request correction of inaccurate or incomplete data</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-primary font-bold">•</span>
                                    <span>Request deletion of your data (subject to legal requirements)</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-primary font-bold">•</span>
                                    <span>Object to or restrict certain data processing</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-primary font-bold">•</span>
                                    <span>Withdraw consent at any time</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-primary font-bold">•</span>
                                    <span>Lodge a complaint with the Office of the Data Protection Commissioner</span>
                                </li>
                            </ul>
                        </div>
                    </div>

                    <div className="group bg-white dark:bg-slate-900 p-8 rounded-2xl border border-border-color dark:border-slate-800 shadow-sm">
                        <div className="flex items-center gap-4 mb-6">
                            <div className="size-12 rounded-xl bg-accent/20 text-accent flex items-center justify-center">
                                <FaDatabase className="text-2xl" />
                            </div>
                            <h3 className="text-2xl font-black text-text-main dark:text-white">7. Data Retention</h3>
                        </div>
                        <div className="space-y-4 text-secondary dark:text-slate-400">
                            <p>We retain your data only as long as necessary:</p>
                            <ul className="space-y-2 ml-4">
                                <li className="flex items-start gap-2">
                                    <span className="text-accent font-bold">•</span>
                                    <span>Active accounts: Data retained while your account is active</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-accent font-bold">•</span>
                                    <span>Medical records: Retained for 7 years as per healthcare regulations</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-accent font-bold">•</span>
                                    <span>Deleted accounts: Personal data removed within 30 days (except legally required records)</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-accent font-bold">•</span>
                                    <span>Analytics data: Anonymized and retained for service improvement</span>
                                </li>
                            </ul>
                        </div>
                    </div>

                    <div className="group bg-white dark:bg-slate-900 p-8 rounded-2xl border border-border-color dark:border-slate-800 shadow-sm">
                        <div className="flex items-center gap-4 mb-6">
                            <div className="size-12 rounded-xl bg-success/20 text-success flex items-center justify-center">
                                <FaUserShield className="text-2xl" />
                            </div>
                            <h3 className="text-2xl font-black text-text-main dark:text-white">8. Children's Privacy</h3>
                        </div>
                        <div className="space-y-4 text-secondary dark:text-slate-400">
                            <p>Protecting young patients is important to us:</p>
                            <ul className="space-y-2 ml-4">
                                <li className="flex items-start gap-2">
                                    <span className="text-success font-bold">•</span>
                                    <span>Our services are not directed to children under 13</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-success font-bold">•</span>
                                    <span>Parents or guardians must create accounts for minors</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-success font-bold">•</span>
                                    <span>We do not knowingly collect data from children without parental consent</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-success font-bold">•</span>
                                    <span>Contact us if you believe we have data from a child without consent</span>
                                </li>
                            </ul>
                        </div>
                    </div>

                    <div className="group bg-white dark:bg-slate-900 p-8 rounded-2xl border border-border-color dark:border-slate-800 shadow-sm">
                        <div className="flex items-center gap-4 mb-6">
                            <div className="size-12 rounded-xl bg-primary/20 text-primary flex items-center justify-center">
                                <FaEye className="text-2xl" />
                            </div>
                            <h3 className="text-2xl font-black text-text-main dark:text-white">9. Changes to This Policy</h3>
                        </div>
                        <div className="space-y-4 text-secondary dark:text-slate-400">
                            <p>We may update this policy periodically:</p>
                            <ul className="space-y-2 ml-4">
                                <li className="flex items-start gap-2">
                                    <span className="text-primary font-bold">•</span>
                                    <span>Updates will be posted on this page with a new "Last Updated" date</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-primary font-bold">•</span>
                                    <span>Significant changes will be communicated via email or app notification</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-primary font-bold">•</span>
                                    <span>Continued use of our services constitutes acceptance of updated terms</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-primary font-bold">•</span>
                                    <span>We encourage you to review this policy regularly</span>
                                </li>
                            </ul>
                        </div>
                    </div>

                    <div className="group bg-white dark:bg-slate-900 p-8 rounded-2xl border border-border-color dark:border-slate-800 shadow-sm">
                        <div className="flex items-center gap-4 mb-6">
                            <div className="size-12 rounded-xl bg-primary/20 text-primary flex items-center justify-center">
                                <FaEnvelope className="text-2xl" />
                            </div>
                            <h3 className="text-2xl font-black text-text-main dark:text-white">10. Contact Us</h3>
                        </div>
                        <div className="space-y-4 text-secondary dark:text-slate-400">
                            <p>Have questions about this policy? Reach out to us:</p>
                            <div className="grid md:grid-cols-3 gap-4 mt-6">
                                <a href="mailto:support@healthconnect.co.ke" className="flex items-center gap-3 p-4 rounded-xl bg-base-200 hover:bg-primary/10 transition-colors">
                                    <FaEnvelope className="text-primary text-xl" />
                                    <div>
                                        <p className="font-bold text-text-main dark:text-white text-sm">Email</p>
                                        <p className="text-xs">support@healthconnect.co.ke</p>
                                    </div>
                                </a>
                                <a href="tel:+254700000000" className="flex items-center gap-3 p-4 rounded-xl bg-base-200 hover:bg-primary/10 transition-colors">
                                    <FaPhone className="text-primary text-xl" />
                                    <div>
                                        <p className="font-bold text-text-main dark:text-white text-sm">Phone</p>
                                        <p className="text-xs">+254 700 000 000</p>
                                    </div>
                                </a>
                                <div className="flex items-center gap-3 p-4 rounded-xl bg-base-200">
                                    <FaMapMarkerAlt className="text-primary text-xl" />
                                    <div>
                                        <p className="font-bold text-text-main dark:text-white text-sm">Location</p>
                                        <p className="text-xs">Nairobi, Kenya</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </section>

            <section className="py-12 px-6 bg-base-100">
                <div className="max-w-4xl mx-auto">
                    <div className="bg-primary/5 dark:bg-slate-800/50 rounded-2xl p-6 border border-primary/20">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                            <div>
                                <p className="text-sm text-secondary dark:text-slate-400 mb-1">Last Updated</p>
                                <p className="font-bold text-text-main dark:text-white">January 13, 2026</p>
                            </div>
                            <div className="flex flex-wrap gap-4 text-sm">
                                <a href="mailto:support@healthconnect.co.ke" className="flex items-center gap-2 text-primary font-bold hover:underline">
                                    <FaEnvelope /> support@healthconnect.co.ke
                                </a>
                                <a href="tel:+254700000000" className="flex items-center gap-2 text-primary font-bold hover:underline">
                                    <FaPhone /> +254 700 000 000
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            <section className="py-12 px-6 bg-base-200/50">
                <div className="max-w-4xl mx-auto">
                    <div className="bg-primary rounded-2xl p-8 text-center">
                        <h3 className="text-2xl font-black text-white mb-4">By Using HealthConnect</h3>
                        <p className="text-white/80 text-lg mb-0">
                            You acknowledge that you have read, understood, and agree to be bound by this Privacy Policy.
                            If you do not agree, please do not use our services.
                        </p>
                    </div>
                </div>
            </section>

        </div>
    )
}

export default PrivacyPage